import SENTRY from 'Config/sentry';

let sentryApp = '';
let sentryVersion = '';

/**
 * Initializes Sentry and registers unexpected errors
 * @param {string} app - Application name
 * @param {string} version - Application version
 */
export const setupSentry = (app, version) => {
  sentryApp = app;
  sentryVersion = version;

  if (window?.ikea?.sentry && SENTRY.enabled) {
    window.ikea?.sentry.register(
      SENTRY.projectId,
      SENTRY.publicKey,
      app,
      version,
      SENTRY.sampleRate,
      [SENTRY.collectionPattern]
    );
  } else {
    // eslint-disable-next-line no-console
    console.warn('Sentry setup skipped:', `${app}@${version}`);
  }
};

/**
 * Creates an adhoc alert in Sentry. Typically used in try/catch scenarios
 * @param {Error | string | any} exception - The exception to capture
 * @param {Object} [customTags] - Optional custom tags to attach to the error
 * @param {number} [customSampleRate] - Optional custom sample rate for the error
 */
export const captureSentry = (exception, customTags, customSampleRate) => {
  if (window?.ikea?.sentry && SENTRY.enabled) {
    window.ikea.sentry.store(
      exception,
      SENTRY.projectId,
      SENTRY.publicKey,
      sentryApp,
      sentryVersion,
      customSampleRate || SENTRY.sampleRate,
      {},
      null,
      customTags
    );
  } else {
    // eslint-disable-next-line no-console
    console.warn('Sentry capture skipped:', exception);
  }
};

/**
 * Logs an application event to Sentry as an info-level message
 * @param {string} eventType - The type of event (e.g., 'user_action', 'component_mounted')
 * @param {string} eventDescription - A description of what happened
 * @param {Object} [customTags] - Optional custom tags to attach to the event
 * @param {number} [customSampleRate] - Optional custom sample rate for the event
 */
export const logAppEvent = (
  eventType,
  eventDescription,
  customTags = {},
  customSampleRate
) =>
  captureSentry(
    `[${sentryApp}] ${eventDescription}`,
    {
      'wlo.level': 'info',
      'wlo.type': eventType,
      ...customTags
    },
    customSampleRate
  );

/**
 * Logs a component mounted event
 * @param {string} componentName - The name of the component that mounted
 * @param {Object} [customTags] - Optional custom tags to attach to the event
 * @param {number} [customSampleRate] - Optional custom sample rate for the event
 */
export const logComponentMounted = (
  componentName,
  customTags = {},
  customSampleRate
) =>
  logAppEvent(
    'component_mounted',
    `${componentName} mounted`,
    customTags,
    customSampleRate
  );

/**
 * Logs a form configuration used event
 * @param {string} formId - The name of the form configuration that was used
 */
export const logFormConfigurationUsed = formId =>
  logAppEvent('form_configuration_used', `form config used ${formId}`);

/**
 * Logs a form configuration not found event
 * @param {string} formId - The name of the form configuration that is missing
 */
export const logFormConfigurationNotFound = formId =>
  logAppEvent('form_configuration_not_found', `form not found ${formId}`);
